import _memoize from 'lodash/memoize';
import { FULL_HD, KEYBOARD_HEIGHT, KEYBOARD_OFFSET_X, KEYBOARD_OFFSET_Y, KEYBOARD_WIDTH } from '@bedrock-rdk/constants';
import SkeletonRectangle from '../../tornado/skeleton/skeleton.rectangle.component';
import { cacheResolver, makeDefaultStylingContext } from '../../tornado/utils/style';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../../tornado/utils/sizes';
import { DEFAULT_KEYBOARD, ITEMS_PER_ROW, SPECIAL_KEYS } from '../../tornado/keyboard/keyboard.constants';
import { KEY_HEIGHT, KEY_WIDTH, KEYS_GAP } from '../../tornado/keyboard/keyItem/keyItem.style';
import { RADIUS as KEY_BORDER_RADIUS } from '../../tornado/keyboard/keyItem/customer/default/keyItem.constants';
import { OFFSET as SEARCH_FIELD_OFFSET, WIDTH as SEARCH_FIELD_WIDTH } from '../searchField/searchField.style';
import {
  BACKGROUND_RADIUS as SEARCH_FIELD_RADIUS,
  HEIGHT as SEARCH_FIELD_HEIGHT,
} from '../searchField/customer/default/searchField.constants';
import {
  CONTAINER_HEIGHT as HISTORY_HEIGHT,
  CONTAINER_WIDTH as HISTORY_WIDTH,
  HISTORY_POSITION_X,
  HISTORY_POSITION_Y,
  SEARCHED_ITEM_POSITION_Y,
} from '../searchHistory/searchHistory.style';
import { ITEM_HEIGHT, ITEM_MARGIN_BOTTOM, ITEM_WIDTH } from '../searchHistory/searchedItem.style';
import { getContentPadding } from '../../layout/abstractLayout.style';
import { BACKGROUND_COLOR, SEARCH_RESULT_OFFSET_X, SEARCH_RESULT_OFFSET_Y } from '../search.style';

export const defaultContext = makeDefaultStylingContext();

/**
 * NOTE: the search result blocks are indented by the padding SearchResultBlock passes down to them,
 * as it renders with no side navigation of its own
 *
 * @see ../blocks/blocks.component.js
 */
export const getBlockContentPadding = screenSize => getContentPadding(false, screenSize);

/**
 * Repeats the way Keyboard lays its keys out: every symbol takes a single slot, while a special one
 * spans `keyLength` slots, and the whole grid is wrapped by ITEMS_PER_ROW slots per row.
 *
 * @see ../../tornado/keyboard/keyboard.component.js
 */
const makeKeys = screenSize => {
  const cellWidth = KEY_WIDTH[screenSize] + KEYS_GAP;
  const cellHeight = KEY_HEIGHT[screenSize] + KEYS_GAP;
  let slot = 0;

  return DEFAULT_KEYBOARD.map(symbol => {
    const { keyLength = 1 } = SPECIAL_KEYS[symbol] || {};
    const key = {
      type: SkeletonRectangle,
      x: Math.round(cellWidth * (slot % ITEMS_PER_ROW)),
      y: Math.round(cellHeight * Math.floor(slot / ITEMS_PER_ROW)),
      // NOTE: the very same width a special key gets in Keyboard, hence the gap being added once only
      w: keyLength * KEY_WIDTH[screenSize] + (keyLength > 1 ? KEYS_GAP : 0),
      h: KEY_HEIGHT[screenSize],
      radius: KEY_BORDER_RADIUS,
    };

    slot += keyLength;

    return key;
  });
};

/**
 * NOTE: the amount of history entries that fits the container, as the real one is unknown before the data is loaded
 */
export const getHistoryItemsCount = screenSize =>
  Math.floor(
    (HISTORY_HEIGHT[screenSize] - SEARCHED_ITEM_POSITION_Y[screenSize]) /
      (ITEM_HEIGHT[screenSize] + ITEM_MARGIN_BOTTOM[screenSize]),
  );

const makeHistoryItems = screenSize =>
  [...Array(getHistoryItemsCount(screenSize))].map((_, index) => ({
    type: SkeletonRectangle,
    w: ITEM_WIDTH[screenSize],
    h: ITEM_HEIGHT[screenSize],
    y: index * (ITEM_HEIGHT[screenSize] + ITEM_MARGIN_BOTTOM[screenSize]),
  }));

export const getInitialStyle = _memoize(({ screenSize = FULL_HD, colorMode } = defaultContext) => {
  const w = SCREEN_WIDTH[screenSize];
  const h = SCREEN_HEIGHT[screenSize];

  return {
    w,
    h,
    Background: {
      w,
      h,
      color: BACKGROUND_COLOR[colorMode],
    },
    SearchField: {
      type: SkeletonRectangle,
      x: KEYBOARD_OFFSET_X[screenSize],
      y: KEYBOARD_OFFSET_Y[screenSize] - SEARCH_FIELD_HEIGHT[screenSize] - SEARCH_FIELD_OFFSET[screenSize],
      w: SEARCH_FIELD_WIDTH[screenSize],
      h: SEARCH_FIELD_HEIGHT[screenSize],
      radius: SEARCH_FIELD_RADIUS,
    },
    Keyboard: {
      x: KEYBOARD_OFFSET_X[screenSize],
      y: KEYBOARD_OFFSET_Y[screenSize],
      w: KEYBOARD_WIDTH[screenSize],
      h: KEYBOARD_HEIGHT[screenSize],
      Keys: {
        children: makeKeys(screenSize),
      },
    },
    SearchHistory: {
      x: HISTORY_POSITION_X[screenSize],
      y: HISTORY_POSITION_Y[screenSize],
      w: HISTORY_WIDTH[screenSize],
      h: HISTORY_HEIGHT[screenSize],
      clipping: true,
      Items: {
        y: SEARCHED_ITEM_POSITION_Y[screenSize],
        children: makeHistoryItems(screenSize),
      },
    },
    Rows: {
      x: SEARCH_RESULT_OFFSET_X[screenSize] + getBlockContentPadding(screenSize),
      y: SEARCH_RESULT_OFFSET_Y[screenSize],
      w: w - SEARCH_RESULT_OFFSET_X[screenSize] - getBlockContentPadding(screenSize),
      h: h - SEARCH_RESULT_OFFSET_Y[screenSize],
      clipping: true,
    },
  };
}, cacheResolver);
